import { MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { country } from '@/data/select';
import { cnRegion, notInCNRegion, specialRegion } from '@/data/china-region';
import { convertCNCitiesData, convertCNRegionData } from '@/utils/convert';
import { useDmpTranslation } from '@/hooks/useDmpTranslation';
import type { ProfileChangeField } from '@/types/profileChanges';

interface Props {
  field: ProfileChangeField;
  value: string;
  countryCode?: string;
  disabled: boolean;
  error?: string;
  onBlur: () => void;
  onChange: (value: string) => void;
}

export default function AddressFields({ field, value, countryCode, disabled, error, onBlur, onChange }: Props) {
  const { t } = useDmpTranslation('profileChanges');
  const { i18n } = useTranslation();
  const lang = i18n?.language ?? 'en';
  const segments = field.segments ?? [];
  const parts = value.split('@@_@@');
  const isMainland = countryCode ? countryCode.startsWith('+86') : segments.some(segment => segment.key === 'province');
  const countryName = country[lang === 'en' ? 'en' : lang === 'sc' ? 'sc' : 'tc']
    .find(item => item.countryCode === countryCode)?.name ?? '';
  const countryValue = countryCode ? `${countryCode}${countryName ? ` (${countryName})` : ''}` : '';
  const provinces = convertCNRegionData(cnRegion, lang).filter(option => !notInCNRegion.includes(String(option.value)));
  const province = parts[0] ?? '';
  const isMunicipality = specialRegion.includes(province);
  const cities = convertCNCitiesData(cnRegion, lang, province);
  const city = !isMunicipality && parts.length === 3 ? parts[1] : '';
  const detail = parts[parts.length - 1] ?? '';

  return <div className="address">
    <TextField select label={t('countryCode')} fullWidth disabled value={countryValue}>
      {countryValue && <MenuItem value={countryValue}>{countryValue}</MenuItem>}
    </TextField>
    {isMainland ? <>
      <TextField select label={t('fields.province')} fullWidth disabled={disabled}
        value={province} onBlur={onBlur} error={!!error} helperText={error}
        onChange={event => {
          const nextProvince = event.target.value;
          const nextCity = convertCNCitiesData(cnRegion, lang, nextProvince)[0]?.value?.toString() ?? '';
          onChange(specialRegion.includes(nextProvince)
            ? [nextProvince, detail].join('@@_@@')
            : [nextProvince, nextCity, detail].join('@@_@@'));
        }}>
        {province && !provinces.some(option => option.value === province) && <MenuItem value={province}>{province}</MenuItem>}
        {provinces.map(option => <MenuItem key={option.value} value={option.value}>{option.text}</MenuItem>)}
      </TextField>
      {!isMunicipality && <TextField select label={t('fields.city')} fullWidth disabled={disabled}
        value={city} onBlur={onBlur} onChange={event => onChange([province, event.target.value, detail].join('@@_@@'))}>
        {city && !cities.some(option => option.value === city) && <MenuItem value={city}>{city}</MenuItem>}
        {cities.map(option => <MenuItem key={option.value} value={option.value}>{option.text}</MenuItem>)}
      </TextField>}
      <TextField label={t('fields.address_floor')} fullWidth disabled={disabled}
        value={detail} onBlur={onBlur} inputProps={{ maxLength: field.max_length }}
        onChange={event => onChange((isMunicipality ? [province, event.target.value] : [province, city, event.target.value])
          .map(part => part.replace(/@@_@@/g, '')).join('@@_@@'))} />
    </> : segments.map((segment, index) => <TextField key={segment.key}
      label={t(`fields.${segment.key}` as 'fields.address_floor')} fullWidth disabled={disabled}
      value={parts[index] ?? ''} onBlur={onBlur} error={!!error} helperText={index === 0 ? error : undefined}
      inputProps={{ maxLength: field.max_length }}
      onChange={event => {
        const next = [...parts];
        next[index] = event.target.value.replace(/@@_@@/g, '');
        onChange(next.join('@@_@@'));
      }} />)}
  </div>;
}
